/*
 * File: components/leave/HolidayTable.js
 * Mô tả: Component hiển thị thông tin tổng quan và chi tiết ngày nghỉ phép (Giao diện thẻ Bento/Light Mode).
 * Cập nhật: Tự động trừ phép tồn nếu ghi chú chứa từ khóa "2025".
 */
import React, { useMemo } from 'react';

// Helper format số
const formatNumber = (num) => num ? parseFloat(num).toString() : '0';

export default function HolidayTable({ data, summary }) {
    // Lấy số giờ tồn năm trước ban đầu từ Backend gửi sang
    const initialCarriedOver = summary?.carriedOverHours || 0;

    // Tính toán số giờ phép tồn ĐÃ SỬ DỤNG tự động theo năm
    const usedCarriedOver = useMemo(() => {
        if (!data || data.length === 0) return 0;
        
        return data.reduce((total, row) => {
            const note = (row.memo || row.reason || '').toString().toLowerCase();
            
            // Lấy năm của ngày nghỉ, trừ đi 1 để ra năm trước (vd: 2027 -> 2026)
            const recordYear = row.date ? new Date(row.date).getFullYear() : new Date().getFullYear();
            const prevYearStr = (recordYear - 1).toString();

            // Kiểm tra từ khóa: chứa số năm trước (vd '2026') HOẶC chứa chữ 'tồn'
            if (note.includes(prevYearStr) || note.includes('tồn')) {
                return total + (parseFloat(row.hours) || 0);
            }
            return total;
        }, 0);
    }, [data]);

    // Số giờ tồn thực tế còn lại để hiển thị (không nhỏ hơn 0)
    const currentCarriedOver = Math.max(0, initialCarriedOver - usedCarriedOver);

    return (
        <div className="space-y-6">
            
            {/* Header Thống kê phép - Giao diện thẻ Card màu Tím */}
            {summary.isCurrentYear ? (
                <div className="bg-gradient-to-br from-purple-50 to-white border border-purple-100 p-5 rounded-3xl relative overflow-hidden shadow-sm">
                    {/* Hiệu ứng mờ góc */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-300/20 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
                    
                    <div>
                        <p className="font-bold text-sm text-purple-900 mb-1">Quỹ phép năm hiện có</p>
                        <div className="flex items-baseline">
                            <span className="text-5xl font-black text-purple-600 tracking-tight">{summary?.remaining || 0}</span>
                            <span className="text-sm font-bold text-purple-800 ml-1.5">Giờ</span>
                        </div>
                    </div>
                    
                    {/* --- HIỂN THỊ DÒNG PHÉP TỒN (NẾU CÓ BAN ĐẦU) --- */}
                    {initialCarriedOver > 0 && (
                        <div className="mt-4 inline-flex items-center gap-2 bg-white/80 px-3.5 py-2 rounded-xl border border-purple-200/60 shadow-sm w-fit backdrop-blur-sm">
                            <span className="text-orange-500 text-base">💡</span>
                            <span className="text-xs font-medium text-gray-600">
                                Tồn năm trước: <span className="font-black text-orange-600">{formatNumber(currentCarriedOver)} Giờ</span>
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-gray-50 border border-gray-200 text-gray-600 p-4 rounded-2xl text-sm text-center font-medium shadow-sm">
                    Dữ liệu phép năm này là lịch sử cũ.
                </div>
            )}

            {/* Bảng chi tiết ngày nghỉ chuyển thành List Cards */}
            <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-xl bg-gray-100 text-gray-500 flex items-center justify-center text-lg">📅</span>
                    Lịch sử nghỉ phép
                </h3>
                
                {(!data || data.length === 0) ? (
                    <div className="bg-gray-50 rounded-3xl border border-gray-200 p-8 text-center border-dashed">
                        <span className="text-4xl mb-3 block grayscale opacity-40">🌴</span>
                        <p className="text-gray-500 text-sm font-medium">Chưa có dữ liệu nghỉ phép nào trong năm nay.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {data.map((row, index) => {
                            // Xử lý phân loại Note để hiển thị Tag động
                            const note = (row.memo || row.reason || '').toString().trim();
                            const noteLower = note.toLowerCase();
                            
                            // Lấy năm trước dựa trên ngày nghỉ của dòng này
                            const recordYear = row.date ? new Date(row.date).getFullYear() : new Date().getFullYear();
                            const prevYearStr = (recordYear - 1).toString();
                            
                            // Nhận diện nếu chứa năm trước hoặc chứa chữ 'tồn'
                            const isCarriedOver = noteLower.includes(prevYearStr) || noteLower.includes('tồn');
                            const isKhangCong = noteLower === 'khang cong';

                            return (
                                <div key={index} className="bg-white border border-gray-200 shadow-sm p-4 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:border-purple-300 transition-colors">
                                    <div className="flex-1 w-full">
                                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                            <p className="font-bold text-gray-900 text-base">
                                                {new Date(row.date).toLocaleDateString('vi-VN', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' })}
                                            </p>
                                            
                                            {/* Hiển thị Tag Tồn động theo năm */}
                                            {isCarriedOver && (
                                                <span className="px-2 py-0.5 rounded-md bg-orange-50 text-orange-600 text-[10px] font-bold border border-orange-100">
                                                    Phép tồn {prevYearStr}
                                                </span>
                                            )}
                                            {/* Hiển thị Tag KC nếu có */}
                                            {isKhangCong && (
                                                <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10px] font-bold border border-indigo-100">
                                                    Kháng công
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Đã xóa phần hiển thị thời gian giờ (timeUp / timeDown) ở đây */}
                                        
                                        {/* Hiển thị box ghi chú nếu có (Và bỏ qua nếu là Kháng công vì đã có tag) */}
                                        {note && !isKhangCong && (
                                            <div className="mt-3 bg-gray-50 p-2.5 rounded-xl border border-gray-100 inline-block w-full">
                                                <p className="text-xs text-gray-600">
                                                    <span className="font-bold text-gray-800">Lý do: </span> 
                                                    {note}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Khối hiển thị số giờ nghỉ bên phải */}
                                    <div className="text-right shrink-0 bg-purple-50 px-3 py-2 rounded-xl border border-purple-100 self-end sm:self-center w-full sm:w-auto flex sm:block justify-between items-center">
                                        <span className="block text-[10px] text-purple-500 font-bold uppercase mb-0.5 sm:mb-1">Đã nghỉ</span>
                                        <span className="font-black text-purple-700 text-lg">{row.hours} <span className="text-[11px] font-bold text-purple-400">giờ</span></span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}